// Bottom-Strip mit Sync-Status, Undo/Redo, Zoom-Slider, Object-Count.

import { useTranslation } from "react-i18next";
import type { RealtimeStatus } from "../realtime/use-board-doc.js";
import type { UndoApi } from "./use-undo-manager.js";
import { ZOOM_MAX, ZOOM_MIN } from "./use-viewport.js";

interface BottomStripProps {
  status: RealtimeStatus | "—";
  objectCount: number;
  selectedCount: number;
  zoom: number;
  setZoom: (z: number) => void;
  resetView: () => void;
  undo: UndoApi;
}

export function BottomStrip({
  status,
  objectCount,
  selectedCount,
  zoom,
  setZoom,
  resetView,
  undo,
}: BottomStripProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="canvas-bottom-strip">
      <span className={`canvas-status canvas-status-${status}`}>{status}</span>
      <span className="canvas-counter">{t("canvas.objects", { count: objectCount })}</span>
      {selectedCount > 0 && (
        <span className="canvas-selection-count">
          {t("canvas.selectionCount", { count: selectedCount })}
        </span>
      )}
      <span className="canvas-strip-divider" aria-hidden="true" />
      <button
        type="button"
        className="strip-btn"
        onClick={undo.undo}
        disabled={!undo.canUndo}
        title={`${t("canvas.undo")} (Ctrl+Z)`}
      >
        ↶
      </button>
      <button
        type="button"
        className="strip-btn"
        onClick={undo.redo}
        disabled={!undo.canRedo}
        title={`${t("canvas.redo")} (Ctrl+Shift+Z)`}
      >
        ↷
      </button>
      <span className="canvas-strip-divider" aria-hidden="true" />
      <label className="canvas-zoom-control">
        <span className="canvas-zoom-value">{Math.round(zoom * 100)}%</span>
        <input
          type="range"
          min={ZOOM_MIN * 100}
          max={ZOOM_MAX * 100}
          step={5}
          value={Math.round(zoom * 100)}
          onChange={(e) => setZoom(Number(e.target.value) / 100)}
          aria-label={t("canvas.zoom")}
        />
        <button type="button" className="strip-btn" onClick={resetView}>
          {t("canvas.reset")}
        </button>
      </label>
    </div>
  );
}
